import { Lock, Trash2 } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import type { CollabFileState } from '../client/useYjs.js'

/**
 * #823 — why an open editor stopped accepting edits: its file was moved to the bin (the last text stays on screen,
 * read-only, so the person can copy out what they were writing), a folder above it is being deleted (#781), or this person's access changed to view only (#1315).
 * A strip at the top of the editor, never an overlay: the content below it is the point.
 */
export function FileStateBanner({ state, office = false }: { state: CollabFileState; office?: boolean }) {
  const { t } = useTranslation('collab')
  if (!state) return null
  if (state.kind === 'read-only') {
    return (
      <div
        role="status"
        data-testid="editor-file-read-only"
        className="flex items-start gap-2 px-3 py-2 text-sm themed-bg-secondary themed-text border-b themed-border shrink-0"
      >
        <Lock size={16} className="mt-0.5 shrink-0 opacity-70" />
        <div className="min-w-0">
          <div className="font-medium">{t('fileState.readOnly')}</div>
          <div className="themed-text-muted text-xs">{t('fileState.readOnlyHint')}</div>
        </div>
      </div>
    )
  }
  const deleted = state.kind === 'deleted'
  const title = deleted
    ? (state.by ? t('fileState.deletedBy', { name: state.by }) : t('fileState.deleted'))
    : t('fileState.beingDeleted')
  const hint = deleted
    ? (office ? t('fileState.officeDeletedHint') : t('fileState.deletedHint'))
    : (office ? t('fileState.officeBeingDeletedHint') : t('fileState.beingDeletedHint'))
  const Icon = deleted ? Trash2 : Lock
  return (
    <div
      role="status"
      data-testid={deleted ? 'editor-file-deleted' : 'editor-file-being-deleted'}
      className="flex items-start gap-2 px-3 py-2 text-sm themed-bg-secondary themed-text border-b themed-border shrink-0"
    >
      <Icon size={16} className="mt-0.5 shrink-0 opacity-70" />
      <div className="min-w-0">
        <div className="font-medium">{title}</div>
        <div className="themed-text-muted text-xs">{hint}</div>
      </div>
    </div>
  )
}
